import { ArrowRight, ArrowLeft, Shield, Settings, Zap, Activity, GitBranch, Monitor, CheckCircle, Code, Lock, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from 'react';
import PageLayout from '@/components/PageLayout';
import { Card, CardContent } from "@/components/ui/card";
import SectionDivider from '@/components/SectionDivider';
import SEO from '@/components/SEO';
import StructuredData from '@/components/StructuredData';

const DevSecOps = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeService, setActiveService] = useState(0);

  const devSecOpsServices = [
    {
      icon: <GitBranch className="w-8 h-8" />,
      title: "Pipeline Security Integration",
      subtitle: "Embedding security into CI/CD pipelines",
      description: "Seamlessly integrate security controls and testing into your development pipelines, ensuring security becomes an automated part of your software delivery lifecycle.",
      gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      borderColor: "rgba(16, 185, 129, 0.3)",
      iconColor: "#10b981",
      capabilities: [
        {
          title: "CI/CD Security Gates",
          description: "Automated security checkpoints preventing vulnerable code progression"
        },
        {
          title: "Code Analysis Integration",
          description: "SAST, DAST, and SCA tools embedded into build processes"
        },
        {
          title: "Container Security Scanning",
          description: "Automated container image vulnerability and compliance scanning"
        },
        {
          title: "Infrastructure as Code Security",
          description: "Policy validation for Terraform, CloudFormation, and Kubernetes configs"
        }
      ]
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Security Automation",
      subtitle: "Automating security testing and validation",
      description: "Comprehensive automation of security processes to reduce manual overhead while improving security coverage and consistency across your development lifecycle.",
      gradient: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
      borderColor: "rgba(34, 197, 94, 0.3)",
      iconColor: "#22c55e",
      capabilities: [
        {
          title: "Vulnerability Management Automation",
          description: "Automated vulnerability discovery, correlation, and intelligent remediation"
        },
        {
          title: "Security Tool Orchestration",
          description: "Multiple security tools coordination eliminating noise and duplicates"
        },
        {
          title: "Automated Remediation",
          description: "Self-healing controls automatically fixing vulnerabilities and misconfigurations"
        },
        {
          title: "Security Metrics & Reporting",
          description: "Real-time dashboards and automated reporting for teams and executives"
        }
      ]
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Compliance & Governance",
      subtitle: "Continuous compliance and policy enforcement",
      description: "Establish comprehensive governance frameworks that ensure continuous compliance while maintaining development velocity through policy-as-code approaches.",
      gradient: "linear-gradient(135deg, #15803d 0%, #166534 100%)",
      borderColor: "rgba(21, 128, 61, 0.3)",
      iconColor: "#15803d",
      capabilities: [
        {
          title: "Policy as Code Implementation",
          description: "Security policies defined and enforced through code across environments"
        },
        {
          title: "Compliance Automation",
          description: "Automated compliance checking for SOC 2, ISO 27001, HIPAA frameworks"
        },
        {
          title: "Governance Frameworks",
          description: "DevSecOps governance balancing security with development productivity"
        },
        {
          title: "Continuous Monitoring",
          description: "Real-time security posture monitoring with automated alerting"
        }
      ]
    }
  ];

  const benefits = [
    {
      icon: <GitBranch className="w-12 h-12" />,
      title: "Seamless Integration",
      description: "Security that flows naturally with your development process without disrupting productivity",
      color: "#10b981"
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: "Accelerated Delivery",
      description: "Faster, more secure releases through intelligent automation and early vulnerability detection",
      color: "#22c55e"
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: "Proactive Protection",
      description: "Shift security left to identify and fix issues before they reach production environments",
      color: "#15803d"
    }
  ];

  return (
    <PageLayout>
      <SEO 
        title="DevSecOps Consulting | CI/CD Pipeline Security Integration Services"
        description="Expert DevSecOps consulting and implementation services. Integrate security into CI/CD pipelines with automated testing, compliance as code, and shift-left security practices. Transform your development workflow with secure DevOps practices."
        keywords={[
          'DevSecOps consulting',
          'CI/CD pipeline security',
          'DevSecOps implementation',
          'pipeline security integration',
          'shift left security',
          'DevSecOps services',
          'automated security testing',
          'compliance as code',
          'secure DevOps',
          'CI/CD security automation',
          'container security DevOps',
          'infrastructure as code security',
          'DevSecOps transformation',
          'security automation pipeline',
          'DevOps security consulting'
        ]}
      />
      <StructuredData type="devSecOps" />
      
      {/* Hero Section */}
      <section
        className="pt-24 pb-16 relative overflow-hidden"
        style={{
          background: "#0f0f17",
          color: "#fff",
        }}
      >
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden z-0">
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 10% 20%, rgba(16, 185, 129, 0.15) 0%, transparent 50%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 90% 80%, rgba(34, 197, 94, 0.15) 0%, transparent 50%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 50%, rgba(21, 128, 61, 0.1) 0%, transparent 70%)" }} />
        </div>

        <div className="container mx-auto px-8 relative z-10" style={{ maxWidth: 1280 }}>
          <div className="max-w-6xl mx-auto">
            <Link
              to="/services"
              className="inline-flex items-center mb-8 transition-colors group rounded-full px-5 py-2 font-semibold shadow-lg"
              style={{
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                color: "#fff",
                fontWeight: 600,
                fontSize: 16,
                borderRadius: "999px",
                boxShadow: "0 4px 24px 0 rgba(16, 185, 129, 0.18)"
              }}
            >
              <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Back to Services
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div
                className="inline-block mb-4 px-6 py-3 text-sm font-medium border shadow-lg"
                style={{
                  background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  color: "#ffffff",
                  borderRadius: "999px",
                  borderColor: "rgba(16, 185, 129, 0.3)",
                  borderWidth: 1.5,
                  boxShadow: "0 4px 24px 0 rgba(16, 185, 129, 0.25)",
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                }}
              >
                <GitBranch className="w-4 h-4 inline mr-2" />
                DevSecOps
              </div>
              <h1
                className="mb-6"
                style={{
                  fontSize: "clamp(2.5rem, 6vw, 3.75rem)",
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: "#fff",
                  letterSpacing: "-0.025em",
                }}
              >
                <span
                  className="block"
                  style={{
                    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    color: "#10b981",
                    fontWeight: 700,
                  }}
                >
                  DevSecOps That Actually Works
                </span>
                <span className="block mt-2" style={{ fontSize: "0.7em", color: "#e5e7eb" }}>
                  Security Automation Developers Love
                </span>
              </h1>
              <p
                className="max-w-3xl mx-auto"
                style={{
                  fontSize: "1.125rem",
                  color: "#d1d5db",
                  lineHeight: 1.7,
                  fontWeight: 400,
                }}
              >
                Custom security automation that makes developers faster while executives sleep better. 
                From foundation to enterprise transformation - we build DevSecOps solutions you actually own.
              </p>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section
        className="py-16 px-8"
        style={{
          background: "#0f0f17",
        }}
      >
        <div className="container mx-auto" style={{ maxWidth: 1280 }}>
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2
                style={{
                  fontSize: "2.25rem",
                  fontWeight: 700,
                  color: "#fff",
                  letterSpacing: "-0.01em",
                  marginBottom: 16,
                }}
              >
                Comprehensive DevSecOps Services
              </h2>
              <p
                style={{
                  fontSize: "1.125rem",
                  color: "#d1d5db",
                  maxWidth: 800,
                  margin: "0 auto",
                }}
              >
                Embed security throughout your development lifecycle
              </p>
            </motion.div>

            {/* Service Navigation */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
              {devSecOpsServices.map((service, index) => (
                <button
                  key={index}
                  onClick={() => setActiveService(index)}
                  className={`px-3 py-4 rounded-lg font-medium transition-all duration-300 text-center ${
                    activeService === index ? 'shadow-lg' : 'opacity-70 hover:opacity-100'
                  }`}
                  style={{
                    background: activeService === index ? service.gradient : 'rgba(15, 15, 23, 0.85)',
                    border: `1.5px solid ${activeService === index ? service.borderColor : 'rgba(255, 255, 255, 0.1)'}`,
                    color: "#fff",
                    minHeight: "100px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    padding: "16px 12px"
                  }}
                >
                  <div style={{ flexShrink: 0 }}>
                    {service.icon}
                  </div>
                  <span className="text-xs sm:text-sm leading-tight" style={{ wordBreak: "break-word" }}>
                    {service.title}
                  </span>
                </button>
              ))}
            </div>

            {/* Active Service Details */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeService}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                <Card
                  className="mb-8"
                  style={{
                    background: "rgba(15, 15, 23, 0.85)",
                    border: `2px solid ${devSecOpsServices[activeService].borderColor}`,
                    borderRadius: 16,
                  }}
                >
                  <CardContent className="p-4 sm:p-6 md:p-8">
                    <div className="flex flex-col sm:flex-row items-start mb-8">
                      <div
                        className="flex items-center justify-center mb-4 sm:mb-0 sm:mr-6 flex-shrink-0"
                        style={{
                          width: 64,
                          height: 64,
                          background: devSecOpsServices[activeService].gradient,
                          borderRadius: 16,
                        }}
                      >
                        {devSecOpsServices[activeService].icon}
                      </div>
                      <div className="flex-1">
                        <h3
                          className="text-xl sm:text-2xl md:text-3xl"
                          style={{
                            fontWeight: 700,
                            color: "#fff",
                            marginBottom: 8,
                          }}
                        >
                          {devSecOpsServices[activeService].title}
                        </h3>
                        <p
                          className="text-base sm:text-lg"
                          style={{
                            color: devSecOpsServices[activeService].iconColor,
                            fontWeight: 600,
                            marginBottom: 16,
                          }}
                        >
                          {devSecOpsServices[activeService].subtitle}
                        </p>
                        <p
                          className="text-sm sm:text-base"
                          style={{
                            color: "#d1d5db",
                            lineHeight: 1.7,
                          }}
                        >
                          {devSecOpsServices[activeService].description}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      {devSecOpsServices[activeService].capabilities.map((capability, idx) => (
                        <div
                          key={idx}
                          className="p-4 sm:p-6 rounded-lg"
                          style={{
                            background: "rgba(0, 0, 0, 0.3)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                          }}
                        >
                          <div className="flex items-start">
                            <Settings
                              className="w-5 h-5 mr-3 mt-1 flex-shrink-0"
                              style={{ color: devSecOpsServices[activeService].iconColor }}
                            />
                            <div className="flex-1">
                              <h4
                                className="text-base sm:text-lg"
                                style={{
                                  fontWeight: 600,
                                  color: "#fff",
                                  marginBottom: 8,
                                }}
                              >
                                {capability.title}
                              </h4>
                              <p
                                className="text-xs sm:text-sm"
                                style={{
                                  color: "#9ca3af",
                                  lineHeight: 1.6,
                                }}
                              >
                                {capability.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </AnimatePresence>

            {/* Benefits Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <div 
                    className="flex items-center justify-center mx-auto mb-4" 
                    style={{ 
                      color: benefit.color,
                      width: "48px",
                      height: "48px"
                    }}
                  >
                    {benefit.icon}
                  </div>
                  <h4 style={{ fontWeight: 700, color: "#fff", marginBottom: 8, fontSize: 20 }}>
                    {benefit.title}
                  </h4>
                  <p style={{ color: "#9ca3af", fontSize: 15, lineHeight: 1.6 }}>
                    {benefit.description}
                  </p>
                </div>
              ))}
            </motion.div>

            {/* Pricing & ROI Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              <div className="text-center p-6 rounded-lg" style={{ background: "rgba(15, 15, 23, 0.85)", border: "1px solid rgba(16, 185, 129, 0.2)" }}>
                <h4 style={{ fontWeight: 700, color: "#10b981", marginBottom: 8, fontSize: 18 }}>
                  Investment Range
                </h4>
                <p style={{ color: "#fff", fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
                  $25,000 - $100,000
                </p>
                <p style={{ color: "#9ca3af", fontSize: 14 }}>
                  Based on pipeline complexity and scale
                </p>
              </div>
              
              <div className="text-center p-6 rounded-lg" style={{ background: "rgba(15, 15, 23, 0.85)", border: "1px solid rgba(34, 197, 94, 0.2)" }}>
                <h4 style={{ fontWeight: 700, color: "#22c55e", marginBottom: 8, fontSize: 18 }}>
                  Timeline
                </h4>
                <p style={{ color: "#fff", fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
                  4-12 Weeks
                </p>
                <p style={{ color: "#9ca3af", fontSize: 14 }}>
                  From assessment to full automation
                </p>
              </div>
              
              <div className="text-center p-6 rounded-lg" style={{ background: "rgba(15, 15, 23, 0.85)", border: "1px solid rgba(21, 128, 61, 0.2)" }}>
                <h4 style={{ fontWeight: 700, color: "#15803d", marginBottom: 8, fontSize: 18 }}>
                  Performance Impact
                </h4>
                <p style={{ color: "#fff", fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
                  95% Faster
                </p>
                <p style={{ color: "#9ca3af", fontSize: 14 }}>
                  Secure deployment velocity improvement
                </p>
              </div>
            </motion.div>

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-16 text-center"
              style={{
                background: "rgba(15, 15, 23, 0.85)",
                borderRadius: 16,
                padding: "3rem",
                border: "1px solid rgba(16, 185, 129, 0.2)",
              }}
            >
              <h3
                style={{
                  fontSize: "2rem",
                  fontWeight: 700,
                  color: "#fff",
                  marginBottom: 16,
                }}
              >
                Transform Your Development Pipeline
              </h3>
              <p
                style={{
                  color: "#d1d5db",
                  fontSize: 18,
                  marginBottom: 32,
                  maxWidth: 600,
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                From foundation to enterprise transformation - partner with our DevSecOps experts to build security automation that developers love and executives trust.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contactus"
                  className="inline-flex items-center font-medium transition-all shadow-lg hover:shadow-xl"
                  style={{
                    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    color: "#ffffff",
                    padding: "1rem 2rem",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "1.125rem",
                    boxShadow: "0 4px 24px 0 rgba(16, 185, 129, 0.25)",
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.background = "linear-gradient(135deg, #059669 0%, #047857 100%)";
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = "linear-gradient(135deg, #10b981 0%, #059669 100%)";
                  }}
                >
                  Start DevSecOps Assessment
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center font-medium transition-all"
                  style={{
                    background: "transparent",
                    color: "#10b981",
                    padding: "1rem 2rem",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "1.125rem",
                    border: "1.5px solid #10b981",
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.background = "rgba(16, 185, 129, 0.1)";
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  View All Services
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default DevSecOps;